/**
 * GlitchText
 * ──────────
 * One-shot glitch effect on text.
 * Triggers once on scroll-into-view.
 */

import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from './tokens';

gsap.registerPlugin(ScrollTrigger);

export default function GlitchText({
  children,
  as: Component = 'span',
  className = '',
  style = {},
  duration = 0.4,
  intensity = 1,
  triggerOnce = true,
  ...props
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [hasGlitched, setHasGlitched] = useState(false);
  const [displayText, setDisplayText] = useState(children);
  
  const originalText = typeof children === 'string' ? children : '';
  
  useEffect(() => {
    if (!containerRef.current || prefersReducedMotion() || !originalText) return;
    if (triggerOnce && hasGlitched) return;
    
    const glitchChars = '!@#$%^&*()_+-=[]{}|;:,.<>?/~`0123456789';
    
    const runGlitch = () => {
      if (triggerOnce && hasGlitched) return;
      
      const chars = originalText.split('');
      const iterations = Math.floor(duration * 1000 / 50); // 50ms per frame
      let currentIteration = 0;
      
      const interval = setInterval(() => {
        setDisplayText(
          chars.map((char, index) => {
            if (index < currentIteration / (iterations / chars.length)) {
              return originalText[index];
            }
            if (char === ' ') return ' ';
            return glitchChars[Math.floor(Math.random() * glitchChars.length)];
          }).join('')
        );
        
        currentIteration++;
        
        if (currentIteration >= iterations) {
          clearInterval(interval);
          setDisplayText(originalText);
          setHasGlitched(true);
        }
      }, 50);
      
      // Also animate position
      gsap.to(textRef.current, {
        x: () => (Math.random() - 0.5) * 4 * intensity,
        y: () => (Math.random() - 0.5) * 2 * intensity,
        duration: 0.05,
        repeat: iterations - 1,
        yoyo: true,
        ease: "none",
        onComplete: () => {
          gsap.set(textRef.current, { x: 0, y: 0 });
        },
      });
    };
    
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top 80%",
        onEnter: runGlitch,
        once: triggerOnce,
      });
    }, containerRef);
    
    return () => ctx.revert();
  }, [originalText, duration, intensity, triggerOnce, hasGlitched]);
  
  if (prefersReducedMotion()) {
    return (
      <Component className={className} style={style} {...props}>
        {children}
      </Component>
    );
  }
  
  return (
    <Component
      ref={containerRef}
      className={className}
      style={{
        ...style,
        position: 'relative',
        display: 'inline-block',
      }}
      {...props}
    >
      <span
        ref={textRef}
        style={{
          display: 'inline-block',
          willChange: 'transform',
        }}
      >
        {displayText}
      </span>
    </Component>
  );
}
